import { useState, useEffect, useRef } from "react";
import { useTheme } from "../hooks/useTheme";
import api from "../api/api";
import { Link } from "react-router-dom";
import { FileText, Trash2, Loader2, AlertCircle, CheckCircle2, Clock, ArrowLeft, X, Info, ChevronDown, Eye, ExternalLink } from "lucide-react";
import ResumeReportCard from "../components/ResumeReportCard";
import ResumeAnalysisSkeleton from "../components/layout/ResumeAnalysisSkeleton";
import OrgChecklistTab from "../components/OrgChecklistTab";
import OrgChecklistHistory from "../components/OrgChecklistHistory";

export default function ResumeAnalysis() {
    const { isDarkMode } = useTheme();
    const [resumes, setResumes] = useState([]);
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [selectedResumeId, setSelectedResumeId] = useState("");
    const [jobDescription, setJobDescription] = useState("");
    const [activeTab, setActiveTab] = useState("review");
    const [historySubTab, setHistorySubTab] = useState("reviews");
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);
    const [usage, setUsage] = useState(null);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewName, setPreviewName] = useState("");
    const [deleteAnalysisConfirm, setDeleteAnalysisConfirm] = useState(null);
    const [analysisLabel, setAnalysisLabel] = useState("");
    const [defaultJobDescriptions, setDefaultJobDescriptions] = useState([]);
    const [selectedJdId, setSelectedJdId] = useState("");
    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [resumeRes, analysisRes] = await Promise.all([
                api.get("/resume/my-resumes"),
                api.get("/resume/my-analyses"),
            ]);
            setResumes(resumeRes.data.resumes || []);
            setAnalyses(analysisRes.data.analyses || []);
            try {
                const usageRes = await api.get("/resume/usage");
                setUsage(usageRes.data);
            } catch {
                setUsage(null);
            }
            try {
                const jdRes = await api.get("/resume/job-descriptions");
                setDefaultJobDescriptions(jdRes.data.jobDescriptions || []);
            } catch {
                setDefaultJobDescriptions([]);
            }
        } catch (err) {
            setError("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handlePreviewResume = async (resumeId) => {
        const resume = resumes.find((r) => r.ResumeID === resumeId);
        const fileName = resume?.FileName || "";
        const isPdf = fileName.toLowerCase().endsWith(".pdf");

        try {
            const res = await api.get(`/resume/${resumeId}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);

            if (isPdf) {
                setPreviewUrl(url);
                setPreviewName(resume?.NickName || fileName);
            } else {
                const a = document.createElement("a");
                a.href = url;
                a.download = fileName;
                a.click();
                URL.revokeObjectURL(url);
            }
        } catch {
            setError("Failed to preview resume");
        }
    };

    const closePreview = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPreviewName("");
    };

    const handleDeleteAnalysis = async (analysisId) => {
        try {
            await api.delete(`/resume/analysis/${analysisId}`);
            setDeleteAnalysisConfirm(null);
            fetchData();
        } catch (err) {
            setError("Failed to delete analysis");
            setDeleteAnalysisConfirm(null);
        }
    };

    const handleViewAnalysis = async (analysisId) => {
        setLoadingAnalysis(true);
        try {
            const res = await api.get(`/resume/analysis/${analysisId}`);
            setSelectedAnalysis(res.data);
        } catch (err) {
            setError("Failed to load analysis");
        } finally {
            setLoadingAnalysis(false);
        }
    };

    const pollAnalysis = async (analysisId) => {
        const maxAttempts = 30;
        for (let i = 0; i < maxAttempts; i++) {
            await new Promise((r) => setTimeout(r, 2000));
            try {
                const res = await api.get(`/resume/analysis/${analysisId}`);
                if (res.data.Status === "completed") {
                    setSelectedAnalysis(res.data);
                    setAnalyzing(false);
                    fetchData();
                    return;
                }
                if (res.data.Status === "failed") {
                    setError(res.data.ErrorMessage || "Analysis failed. Please try again.");
                    setAnalyzing(false);
                    fetchData();
                    return;
                }
            } catch {
                // keep polling
            }
        }
        setError("Analysis is taking longer than expected. Check back in Review History.");
        setAnalyzing(false);
        fetchData();
    };

    const handleAnalyze = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!selectedResumeId) {
            setError("Select a resume first");
            return;
        }

        if (!analysisLabel.trim()) {
            setError("Enter a label for this analysis (e.g. employer, location, date)");
            return;
        }

        if (!jobDescription.trim()) {
            setError("Paste the job description");
            return;
        }

        setAnalyzing(true);
        setSelectedAnalysis(null);
        try {
            const res = await api.post("/resume/analyze", {
                resumeId: selectedResumeId,
                jobDescription,
                label: analysisLabel.trim() || undefined,
                timezoneOffset: new Date().getTimezoneOffset(),
            });
            setJobDescription("");
            setSelectedJdId("");
            setAnalysisLabel("");
            setActiveTab("history");
            setHistorySubTab("reviews");
            fetchData();
            pollAnalysis(res.data.analysisId);
        } catch (err) {
            setError(err.response?.data?.error || "Analysis failed");
            setAnalyzing(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return "";
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const statusBadge = (status) => {
        const styles = {
            completed: "bg-primary-100 text-primary-700",
            pending: "bg-amber-100 text-amber-700",
            processing: "bg-blue-100 text-blue-700",
            failed: "bg-red-100 text-red-700",
        };
        return styles[status] || styles.pending;
    };

    if (loading) {
        return <ResumeAnalysisSkeleton isDarkMode={isDarkMode} />;
    }

    return (
        <div className={`min-h-screen w-full ${isDarkMode ? "bg-surface text-slate-100" : "bg-surface text-slate-900"}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">

                <h1 className="text-2xl font-bold mb-6">Resume Analysis</h1>

<div className="flex gap-2 mb-6">
                    <button
                        onClick={() => { setActiveTab("review"); setSelectedAnalysis(null); }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            activeTab === "review"
                                ? "bg-primary-600 text-white"
                                : isDarkMode
                                    ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                                    : "bg-white text-slate-600 hover:bg-slate-100"
                        }`}
                    >
                        <FileText size={16} />
                        Resume Review
                    </button>
                    <button
                        onClick={() => { setActiveTab("checklist"); setSelectedAnalysis(null); }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            activeTab === "checklist"
                                ? "bg-primary-600 text-white"
                                : isDarkMode
                                    ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                                    : "bg-white text-slate-600 hover:bg-slate-100"
                        }`}
                    >
                        <CheckCircle2 size={16} />
                        Resume Checklist
                    </button>
                    <button
                        onClick={() => setActiveTab("history")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            activeTab === "history"
                                ? "bg-primary-600 text-white"
                                : isDarkMode
                                    ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                                    : "bg-white text-slate-600 hover:bg-slate-100"
                        }`}
                    >
                        <Clock size={16} />
                        Review History
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4 flex items-center gap-2">
                        <AlertCircle size={16} />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-primary-50 border border-primary-200 text-primary-700 px-4 py-3 rounded-lg text-sm mb-4 flex items-center gap-2">
                        <CheckCircle2 size={16} />
                        {success}
                    </div>
                )}

                {activeTab === "review" && (
                <>
                <div className={`flex items-start gap-3 px-4 py-3 rounded-xl mb-6 text-sm ${
                    isDarkMode ? "bg-slate-800/60 text-slate-300 border border-slate-700" : "bg-primary-50 text-slate-600 border border-primary-100"
                }`}>
                    <Info size={16} className="shrink-0 mt-0.5 text-primary-500" />
                    <span>Choose a resume from your uploads, add the job description you're targeting, and hit <strong>Analyze Resume</strong> to get your scores.</span>
                </div>

                {usage && (
                    <div className={`flex items-center justify-end gap-2 text-xs mb-4 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                        <span><span className="font-medium">{usage.remaining}</span> of {usage.monthlyLimit} analyses remaining this month</span>
                        <Link
                            to="/membership/plans"
                            className={`font-semibold whitespace-nowrap px-3 py-1 rounded-full transition-colors ${isDarkMode ? "text-primary-200 bg-primary-600 hover:bg-primary-500" : "text-primary-700 bg-primary-100 hover:bg-primary-200"}`}
                        >
                            Upgrade Now
                        </Link>
                    </div>
                )}

                <section className="mb-8">
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <FileText size={20} />
                            Select Resume
                        </h2>

                        <div className="relative" ref={dropdownRef}>
                            <button
                                type="button"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg text-sm transition-colors ${
                                    isDarkMode
                                        ? "bg-slate-700 border-slate-600 text-slate-100 hover:border-primary-500"
                                        : "bg-white border-slate-300 text-slate-900 hover:border-primary-500"
                                }`}
                            >
                                <span className={selectedResumeId ? "" : isDarkMode ? "text-slate-400" : "text-slate-500"}>
                                    {selectedResumeId
                                        ? (resumes.find((r) => String(r.ResumeID) === String(selectedResumeId))?.NickName ||
                                           resumes.find((r) => String(r.ResumeID) === String(selectedResumeId))?.FileName ||
                                           "Select a resume")
                                        : "Select a resume"}
                                </span>
                                <ChevronDown size={16} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                            </button>

                            {dropdownOpen && (
                                <div className={`absolute z-10 mt-1 w-full rounded-lg border shadow-lg ${
                                    isDarkMode ? "bg-slate-700 border-slate-600" : "bg-white border-slate-200"
                                }`}>
                                    {resumes.map((r) => (
                                        <div
                                            key={r.ResumeID}
                                            onClick={() => {
                                                setSelectedResumeId(r.ResumeID);
                                                setDropdownOpen(false);
                                            }}
                                            className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                                                String(selectedResumeId) === String(r.ResumeID)
                                                    ? isDarkMode ? "bg-primary-900/30" : "bg-primary-50"
                                                    : isDarkMode ? "hover:bg-slate-600" : "hover:bg-slate-50"
                                            }`}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <FileText size={16} className="shrink-0 text-primary-500" />
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium truncate">{r.NickName || r.FileName}</p>
                                                    <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                                        {formatFileSize(r.FileSizeBytes)} &middot; {formatDate(r.CreatedOn)}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePreviewResume(r.ResumeID);
                                                }}
                                                title="Preview"
                                                className="shrink-0 p-1.5 rounded-lg transition-colors text-slate-400 hover:text-primary-500"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </div>
                                    ))}

                                    <Link
                                        to="/preferences" state={{ scrollTo: "resumes" }}
                                        onClick={() => setDropdownOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-3 transition-colors border-t ${
                                            isDarkMode
                                                ? "border-slate-600 hover:bg-slate-600 text-primary-400"
                                                : "border-slate-100 hover:bg-primary-50 text-primary-600"
                                        }`}
                                    >
                                        <ExternalLink size={16} />
                                        <span className="text-sm font-medium">Manage Resumes</span>
                                    </Link>
                                </div>
                            )}
                        </div>

                        {resumes.length === 0 && (
                            <Link
                                to="/preferences" state={{ scrollTo: "resumes" }}
                                className={`mt-3 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-lg text-sm font-medium transition-colors ${
                                    isDarkMode
                                        ? "border-slate-600 text-slate-300 hover:border-primary-500 hover:text-primary-400"
                                        : "border-slate-300 text-slate-500 hover:border-primary-500 hover:text-primary-600"
                                }`}
                            >
                                <ExternalLink size={16} />
                                Upload a resume in My Preferences
                            </Link>
                        )}
                    </div>
                </section>

                <section className="mb-8">
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <FileText size={20} />
                            Job Description
                        </h2>

                        <form onSubmit={handleAnalyze}>
                            <div className="mb-4">
                                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                                    Analysis Label
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. Google, Senior SWE, Austin TX"
                                    value={analysisLabel}
                                    onChange={(e) => setAnalysisLabel(e.target.value)}
                                    maxLength={200}
                                    className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                        isDarkMode
                                            ? "bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400"
                                            : "border-slate-300 text-black placeholder-slate-400"
                                    }`}
                                />
                            </div>

                            {defaultJobDescriptions.length > 0 && (
                                <div className="mb-4">
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                                        Quick Fill from Template
                                    </label>
                                    <select
                                        value={selectedJdId}
                                        onChange={(e) => {
                                            const id = e.target.value;
                                            setSelectedJdId(id);
                                            if (!id) return;
                                            const jd = defaultJobDescriptions.find(j => String(j.JobDescriptionID) === id);
                                            if (!jd) return;
                                            const parts = [jd.RoleName, "", jd.RoleSummary];
                                            if (jd.Responsibilities) parts.push("", "Key Responsibilities:", jd.Responsibilities.replace(/;\s*/g, "\n- "));
                                            if (jd.RequiredSkills) parts.push("", "Required Skills:", jd.RequiredSkills.replace(/;\s*/g, "\n- "));
                                            if (jd.PreferredMajors) parts.push("", "Preferred Majors: " + jd.PreferredMajors);
                                            if (jd.TypicalIndustries) parts.push("", "Typical Industries: " + jd.TypicalIndustries);
                                            setJobDescription(parts.join("\n"));
                                        }}
                                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                            isDarkMode
                                                ? "bg-slate-700 border-slate-600 text-slate-100"
                                                : "border-slate-300 text-black"
                                        }`}
                                    >
                                        <option value="">Select a role to auto-fill...</option>
                                        {defaultJobDescriptions.map(jd => (
                                            <option key={jd.JobDescriptionID} value={jd.JobDescriptionID}>
                                                {jd.RoleName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <textarea
                                placeholder="Paste the full job description here, or select a template above..."
                                value={jobDescription}
                                onChange={(e) => setJobDescription(e.target.value)}
                                rows={6}
                                className={`w-full px-3 py-2 border rounded-lg text-sm resize-none ${
                                    isDarkMode
                                        ? "bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400"
                                        : "border-slate-300 text-black placeholder-slate-400"
                                }`}
                            />

                            <button
                                type="submit"
                                disabled={analyzing || !selectedResumeId || !analysisLabel.trim() || !jobDescription.trim()}
                                className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                            >
                                {analyzing ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Analyzing...
                                    </>
                                ) : (
                                    "Analyze Resume"
                                )}
                            </button>
                        </form>
                    </div>
                </section>
                </>
                )}

                {activeTab === "history" && (
                    <div className="flex gap-2 mb-4">
                        {[
                            { key: "reviews", label: "Resume Reviews" },
                            { key: "checklists", label: "Resume Checklists" },
                        ].map((t) => (
                            <button
                                key={t.key}
                                onClick={() => { setHistorySubTab(t.key); setSelectedAnalysis(null); }}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                                    historySubTab === t.key
                                        ? "bg-primary-600 text-white"
                                        : isDarkMode ? "bg-slate-700 text-slate-300 hover:bg-slate-600" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                )}

                {activeTab === "history" && historySubTab === "reviews" && !selectedAnalysis && (
                    <section>
                        {analyzing && (
                            <div className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-4 text-sm ${
                                isDarkMode ? "bg-primary-900/30 border border-primary-700 text-primary-300" : "bg-primary-50 border border-primary-200 text-primary-700"
                            }`}>
                                <Loader2 size={16} className="animate-spin shrink-0" />
                                Analyzing your resume — this usually takes 10-15 seconds...
                            </div>
                        )}
                        <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold flex items-center gap-2">
                                    <Clock size={20} />
                                    Review History
                                </h2>
                            </div>

                            {analyses.length > 0 ? (
                                <div className="space-y-3">
                                    {analyses.map((a) => (
                                        <div
                                            key={a.AnalysisID}
                                            onClick={() => handleViewAnalysis(a.AnalysisID)}
                                            className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                                                isDarkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-50 hover:bg-slate-100"
                                            }`}
                                        >
                                            <div className="min-w-0 mr-3">
                                                {a.Label && (
                                                    <p className="text-sm font-semibold truncate">{a.Label}</p>
                                                )}
                                                <p className={`text-sm ${a.Label ? "text-xs" : "font-medium"} truncate ${a.Label ? (isDarkMode ? "text-slate-400" : "text-slate-500") : ""}`}>
                                                    {a.ResumeLabel || a.FileName}
                                                </p>
                                                {a.TargetJob && (
                                                    <p className={`text-xs font-medium truncate ${isDarkMode ? "text-primary-400" : "text-primary-600"}`}>
                                                        {a.TargetJob}
                                                    </p>
                                                )}
                                                <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                                    {formatDate(a.CreatedOn)}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge(a.Status)}`}>
                                                    {a.Status === "completed" ? "View Report" : a.Status}
                                                </span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteAnalysisConfirm({ id: a.AnalysisID, name: a.Label || a.TargetJob || a.ResumeLabel || a.FileName });
                                                    }}
                                                    className={`p-1.5 rounded-lg transition-colors ${
                                                        isDarkMode ? "text-slate-400 hover:text-red-400 hover:bg-slate-600" : "text-slate-400 hover:text-red-500 hover:bg-slate-200"
                                                    }`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    No analyses yet. Submit your first resume review to see results here.
                                </p>
                            )}
                        </div>
                    </section>
                )}

                {activeTab === "history" && historySubTab === "reviews" && selectedAnalysis && (
                    <section>
                        <button
                            onClick={() => setSelectedAnalysis(null)}
                            className={`flex items-center gap-2 text-sm font-medium mb-4 transition-colors ${
                                isDarkMode ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <ArrowLeft size={16} />
                            Back to History
                        </button>

                        {loadingAnalysis ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 size={24} className="animate-spin text-primary-500" />
                            </div>
                        ) : selectedAnalysis.Status === "completed" && selectedAnalysis.ResultsJSON ? (
                            <>
                                {selectedAnalysis.Label && (
                                    <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium ${
                                        isDarkMode ? "bg-slate-800 border border-slate-700 text-slate-200" : "bg-slate-50 border border-slate-200 text-slate-700"
                                    }`}>
                                        {selectedAnalysis.Label}
                                    </div>
                                )}
                                <ResumeReportCard analysis={selectedAnalysis} isDarkMode={isDarkMode} />
                            </>
                        ) : (
                            <div className={`rounded-2xl border shadow-sm p-6 text-center ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                                <p className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                                    {selectedAnalysis.ResumeLabel || selectedAnalysis.FileName}
                                </p>
                                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium capitalize ${statusBadge(selectedAnalysis.Status)}`}>
                                    {selectedAnalysis.Status}
                                </span>
                                <p className={`text-sm mt-3 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    {selectedAnalysis.Status === "pending" && "Your analysis is queued and will be processed shortly."}
                                    {selectedAnalysis.Status === "processing" && "Your resume is being analyzed. Check back in a moment."}
                                    {selectedAnalysis.Status === "failed" && (selectedAnalysis.ErrorMessage || "Something went wrong. Please try again.")}
                                </p>
                            </div>
                        )}
                    </section>
                )}

                {activeTab === "checklist" && (
                    <OrgChecklistTab
                        resumes={resumes}
                        isDarkMode={isDarkMode}
                        formatDate={formatDate}
                        formatFileSize={formatFileSize}
                        onPreviewResume={handlePreviewResume}
                    />
                )}

                {activeTab === "history" && historySubTab === "checklists" && (
                    <OrgChecklistHistory
                        isDarkMode={isDarkMode}
                        formatDate={formatDate}
                        statusBadge={statusBadge}
                    />
                )}
            </div>

            {previewUrl && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={closePreview}>
                    <div
                        className={`w-full max-w-4xl h-[85vh] mx-4 rounded-lg border shadow-lg flex flex-col ${
                            isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
                            isDarkMode ? "border-slate-700" : "border-slate-200"
                        }`}>
                            <h3 className="text-sm font-medium truncate">{previewName}</h3>
                            <button onClick={closePreview} className={`p-1 rounded transition-colors ${
                                isDarkMode ? "text-slate-400 hover:text-slate-200 hover:bg-slate-700" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                            }`}>
                                <X size={18} />
                            </button>
                        </div>
                        <iframe
                            src={previewUrl}
                            title="Resume Preview"
                            className="flex-1 w-full rounded-b-lg bg-white"
                        />
                    </div>
                </div>
            )}

            {deleteAnalysisConfirm && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDeleteAnalysisConfirm(null)}>
                    <div
                        className={`w-full max-w-md mx-4 rounded-lg border shadow-lg p-6 ${
                            isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-base font-semibold mb-2">Delete Analysis Report</h3>
                        <p className={`text-sm mb-5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            Are you sure you want to delete the report for <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{deleteAnalysisConfirm.name}</span>? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeleteAnalysisConfirm(null)}
                                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                    isDarkMode
                                        ? "border-slate-600 hover:bg-slate-700 text-slate-300"
                                        : "border-slate-300 hover:bg-slate-50 text-slate-600"
                                }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeleteAnalysis(deleteAnalysisConfirm.id)}
                                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
